import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { formatDate } from "helpers/formatters"
import {
  Info,
  Title,
  Status,
  Help,
  BadgeSuccess,
  BadgeError,
  BadgeGray,
  Actions,
  Cancel,
  Upgrade,
  BankPay,
} from "components/Subscriptions/elements"
import {
  SUBSCRIPTION_STATE_PENDING,
  SUBSCRIPTION_STATE_ACTIVE,
  SUBSCRIPTION_STATE_CANCELLATION_PENDING,
  SUBSCRIPTION_STATE_CANCELED,
  SUBSCRIPTION_STATE_ENDED,
  SUBSCRIPTION_BANK_METHOD,
  PLAN_GOLD,
} from "config"
import { SubscriptionType } from "types"

type Props = {
  subscription: SubscriptionType
}

const TitleComponent = ({ subscription }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { state, end_at, plan } = subscription
  const { plan_group_plan_type } = plan

  const showUpgrade = plan_group_plan_type !== PLAN_GOLD

  const isPending = [SUBSCRIPTION_STATE_PENDING].includes(state)
  const isActive = state === SUBSCRIPTION_STATE_ACTIVE
  const isCancelPending = state === SUBSCRIPTION_STATE_CANCELLATION_PENDING
  const isEnded = [
    SUBSCRIPTION_STATE_CANCELED,
    SUBSCRIPTION_STATE_ENDED,
  ].includes(state)

  const handleSubscriptionUpgrade = () => {
    navigate(`/profile/subscriptions/${subscription.id}/upgrade`)
  }

  const handleSubscriptionCancel = () => {
    navigate(`/profile/subscriptions/${subscription.id}/cancel`)
  }

  const handleBankPay = () => {
    navigate(`/profile/subscriptions/${subscription.id}/pay`)
  }

  return (
    <Info>
      {plan && plan_group_plan_type && (
        <Title variant="h4">
          {t(`features.subscription.plan.type.${plan_group_plan_type}.title`)}
        </Title>
      )}
      <Status>
        {isPending && (
          <BadgeGray>
            {t(`features.subscription.state.${state}.title`)}
            &nbsp;
            {formatDate(end_at)}
          </BadgeGray>
        )}
        {(isActive || isCancelPending) && (
          <BadgeSuccess>
            {t(`features.subscription.state.${state}.title`)}
            &nbsp;
            {formatDate(end_at)}
          </BadgeSuccess>
        )}
        {isEnded && (
          <>
            <BadgeError>
              {t(`features.subscription.state.${state}.title`)}
              &nbsp;
              {formatDate(end_at)}
            </BadgeError>
            <Help>{t(`features.subscription.state.${state}.help`)}</Help>
          </>
        )}
      </Status>
      {isActive && (
        <Actions>
          {subscription.service_type === SUBSCRIPTION_BANK_METHOD && (
            <BankPay onClick={handleBankPay}>
              {t("features.subscription.actions.pay.title")}
            </BankPay>
          )}
          {showUpgrade && (
            <Upgrade onClick={handleSubscriptionUpgrade}>
              {t("features.subscription.actions.upgrade.title")}
            </Upgrade>
          )}
          <Cancel onClick={handleSubscriptionCancel}>
            {t("features.subscription.actions.cancel.title")}
          </Cancel>
        </Actions>
      )}
    </Info>
  )
}

export default TitleComponent
